import CompletenessIndicator from "@/components/CompletenessIndicator/CompletenessIndicator";
import ProductImpactTotals from "@/components/drawers/ProductDrawer/ProductImpactTotals";
import NoImpactBanner from "@/components/NoImpactBanner/NoImpactBanner";
import RootDrawer from "@/components/RootDrawer/RootDrawer";
import MetricSelect from "@/pages/dashboard/components/MetricSelector/MetricSelect";
import YearPicker from "@/pages/dashboard/components/YearPicker/YearPicker";
import { useDashboardYear } from "@/pages/dashboard/hooks/useDateRange";
import {
  extractImpactMetric,
  setLocalStorageMetricKey,
} from "@/pages/dashboard/hooks/useImpactMetric";
import { useDashboardOrderCounts } from "@/pages/dashboard/hooks/usePoSo";
import { PRODUCT_BY, ProductBy } from "@/pages/dashboard/hooks/useProductImpact";
import { ImpactMetricKey } from "@/pages/dashboard/utils/types";
import { useGetProduct } from "@/pages/products/useProducts";
import { productLabel } from "@/utilities/products";
import { Flex, Segmented } from "antd";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import ExportProductLcaAction from "./ExportProductLcaAction";
import styles from "./ProductDrawer.module.scss";
import ProductDrawerContent from "./ProductDrawerContent";

export const PRODUCT_DRAWER_URL_PRODUCT_ID = "productSidebarId";
export const PRODUCT_DRAWER_URL_TYPE = "productSidebarType";
export const PRODUCT_DRAWER_URL_TAB = "productSidebarTab";
export const PRODUCT_DRAWER_URL_BY = "productSidebarBy";

export default function ProductDrawer() {
  const location = useLocation();
  const { pathname } = location;
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const isOpen = Boolean(searchParams.get(PRODUCT_DRAWER_URL_PRODUCT_ID));
  const productId = Number(searchParams.get(PRODUCT_DRAWER_URL_PRODUCT_ID));
  const metric = extractImpactMetric(searchParams.get(PRODUCT_DRAWER_URL_TYPE));
  const by: ProductBy = (String(searchParams.get(PRODUCT_DRAWER_URL_BY)) as ProductBy) || "single";
  const { year } = useDashboardYear();

  const { data, isLoading, isFetching } = useGetProduct({ productId, enabled: isOpen });
  const { data: orderCounts } = useDashboardOrderCounts({ productId });

  if (!productId) return;

  const onClose = () => {
    setSearchParams(
      (searchParams) => {
        searchParams.delete(PRODUCT_DRAWER_URL_PRODUCT_ID);
        searchParams.delete(PRODUCT_DRAWER_URL_TYPE);
        searchParams.delete(PRODUCT_DRAWER_URL_TAB);
        searchParams.delete(PRODUCT_DRAWER_URL_BY);
        return searchParams;
      },
      { replace: true }
    );
  };

  const switchType = (key: ImpactMetricKey) => {
    setSearchParams(
      (searchParams) => {
        searchParams.set(PRODUCT_DRAWER_URL_TYPE, key);
        setLocalStorageMetricKey(key);
        return searchParams;
      },
      { replace: true }
    );

    // if opened from dashboard, also change the metric of the dashboard.
    if (pathname.startsWith("/dashboard/products")) {
      navigate("/dashboard/products/" + key + `?${searchParams.toString()}`);
    }
  };

  const switchBy = (key: string) => {
    setSearchParams(
      (searchParams) => {
        searchParams.set(PRODUCT_DRAWER_URL_BY, key);
        return searchParams;
      },
      { replace: true }
    );
  };

  const headerContent = (
    <div className={styles.header}>
      <div className={styles.selectors}>
        <Flex
          align="center"
          justify="left"
          gap={16}
        >
          <YearPicker />
          <Segmented
            onChange={switchBy}
            value={by}
            options={PRODUCT_BY}
          />
          <MetricSelect
            value={metric.key}
            onChange={switchType}
          />
          <Flex
            justify={"right"}
            gap={8}
            className={styles.completenessIndicatorContainer}
          >
            <CompletenessIndicator />
            <ExportProductLcaAction
              productId={productId}
              metric={metric}
              year={year}
            />
          </Flex>
        </Flex>
      </div>
      <div className={styles.widgets}>
        <ProductImpactTotals
          productId={productId}
          metric={metric}
          by={by}
        />
      </div>
    </div>
  );

  return (
    <RootDrawer
      title={
        <Link
          style={{ color: "inherit" }}
          to={`/products/${data?.product.id}`}
        >
          {productLabel(data?.product.external_id || "", data?.product.name)}
        </Link>
      }
      loading={isLoading || isFetching}
      className={styles.ProductDrawer}
      headerContent={headerContent}
      width={1000}
      open={isOpen}
      onClose={onClose}
    >
      {orderCounts?.total === 0 ? (
        <NoImpactBanner title={`No impact for this product in ${year}`}>
          <span>
            This product was not found in any purchase or sales orders in {year}, so no product
            impact could be calculated. Navigate to a different year, or upload{" "}
            <Link to="/orders/purchase_orders">orders</Link>.
          </span>
        </NoImpactBanner>
      ) : (
        <ProductDrawerContent
          productId={productId}
          metric={metric}
          by={by}
        />
      )}
    </RootDrawer>
  );
}
